import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, IconButton, Chip, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Alert, Divider,
  Menu, MenuItem, LinearProgress, CircularProgress, useTheme, useMediaQuery, Fab, Badge,
  Tabs, Tab, Collapse
} from '@mui/material';
import {
  Add as AddIcon, WhatsApp as WhatsAppIcon, QrCode as QrCodeIcon,
  Edit as EditIcon, Delete as DeleteIcon, MoreVert as MoreVertIcon,
  LinkOff as DisconnectIcon, Circle as CircleIcon, Warning as WarningIcon,
  Close as CloseIcon, Battery80 as BatteryIcon, Sync as SyncIcon,
  Settings as SettingsIcon, Message as MessageIcon, Schedule as ScheduleIcon,
  Block as BlockIcon, CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';
import { atendimentoService, AtendimentoConexao } from '../../services/atendimentoService';
import { env } from '../../utils/env';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const statusConfig: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'info'; icon: React.ReactNode; bgColor: string }> = {
  conectado: { label: 'Conectado', color: 'success', bgColor: '#e8f5e9', icon: <CircleIcon sx={{ fontSize: 10, color: '#2e7d32' }} /> },
  desconectado: { label: 'Desconectado', color: 'error', bgColor: '#ffebee', icon: <CircleIcon sx={{ fontSize: 10, color: '#c62828' }} /> },
  aguardando_qr: { label: 'Aguard. QR', color: 'warning', bgColor: '#fff8e1', icon: <CircleIcon sx={{ fontSize: 10, color: '#e65100' }} /> },
  conectando: { label: 'Conectando', color: 'info', bgColor: '#e3f2fd', icon: <CircleIcon sx={{ fontSize: 10, color: '#0277bd' }} /> },
};

interface DaySchedule {
  enabled: boolean;
  start: string;
  end: string;
}

interface BusinessHours {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
}

const defaultBusinessHours: BusinessHours = {
  monday: { enabled: true, start: '08:00', end: '18:00' },
  tuesday: { enabled: true, start: '08:00', end: '18:00' },
  wednesday: { enabled: true, start: '08:00', end: '18:00' },
  thursday: { enabled: true, start: '08:00', end: '18:00' },
  friday: { enabled: true, start: '08:00', end: '18:00' },
  saturday: { enabled: false, start: '08:00', end: '12:00' },
  sunday: { enabled: false, start: '08:00', end: '12:00' },
};

const defaultOutOfHoursMessage = `👋 Olá! Seja bem-vindo à **Nome do Provedor** 🦊📶

⏰ No momento, estamos fora do nosso horário de atendimento.

💬 Deixe sua mensagem por aqui e nossa equipe retornará assim que possível.

🙏 Obrigado pela compreensão! 💙
**Nome do Provedor — O futuro começa aqui! 🚀**`;

const defaultGreetingMessage = `👋 Olá! Seja bem-vindo à **Nome do Provedor** 🦊📶

Como podemos ajudar você hoje? Por favor, descreva sua solicitação para que nossa equipe possa atendê-lo.`;

const defaultFarewellMessage = `Obrigado pelo contato! 😊
Foi um prazer atendê-lo(a).
Qualquer dúvida, estamos à disposição!
Até mais! 👋`;

const defaultRecuseCallMessage = `📱 Olá! No momento, este número não recebe ligações.
😊 Por favor, deixe sua mensagem por aqui que retornaremos o mais breve possível.

Agradecemos a compreensão! 📝`;

const weekDays: { key: keyof BusinessHours; label: string }[] = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terca-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sabado' },
  { key: 'sunday', label: 'Domingo' },
];

interface FormState {
  nome: string;
  descricao: string;
  ativo: boolean;
  tipo_adapter: 'official_api' | 'whaileys' | 'baileys';
  bot_ativo: boolean;
  flow_id: number | '';
  recuse_call: boolean;
  recuse_call_msg: string;
  msgs_boas_vindas: boolean;
  texto_boas_vindas: string;
  msgs_despedida: boolean;
  texto_despedida: string;
  expediente_ativo: boolean;
  expediente_horarios: BusinessHours;
  texto_fora_expediente: string;
  expediente_ignorar_saudacao: boolean;
  expediente_quando_enviar: 'novo_ticket' | 'nova_mensagem';
}
const formVazio: FormState = {
  nome: '',
  descricao: '',
  ativo: true,
  tipo_adapter: 'whaileys',
  bot_ativo: false,
  flow_id: '',
  recuse_call: false,
  recuse_call_msg: '',
  msgs_boas_vindas: false,
  texto_boas_vindas: '',
  msgs_despedida: false,
  texto_despedida: '',
  expediente_ativo: false,
  expediente_horarios: defaultBusinessHours,
  texto_fora_expediente: '',
  expediente_ignorar_saudacao: false,
  expediente_quando_enviar: 'novo_ticket'
};

// ── Componente principal ──────────────────────────────────────────────────────

const AtendimentoConexoesTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [conexoes, setConexoes] = useState<AtendimentoConexao[]>([]);
  const [fluxos, setFluxos] = useState<Array<{ id: number; name: string; active: boolean }>>([]);
  const [sucessoMsg, setSucessoMsg] = useState('');
  const [erroMsg, setErroMsg] = useState('');
  const [loading, setLoading] = useState(true);

  // Dialog CRUD
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState<AtendimentoConexao | null>(null);
  const [form, setForm] = useState<FormState>(formVazio);
  const [dialogTab, setDialogTab] = useState(0);

  // Dialog QR
  const [qrDialog, setQrDialog] = useState(false);
  const [qrConexao, setQrConexao] = useState<AtendimentoConexao | null>(null);
  const [qrCodeImg, setQrCodeImg] = useState<string | null>(null);
  const activeQrTokenRef = useRef<string | null>(null);
  const [qrGirando, setQrGirando] = useState(false);

  // Dialog exclusão
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteAlvo, setDeleteAlvo] = useState<AtendimentoConexao | null>(null);

  // Menu ações
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAlvo, setMenuAlvo] = useState<AtendimentoConexao | null>(null);

  const socketRef = useRef<Socket | null>(null);

  const carregarConexoes = async () => {
    try {
      setLoading(true);
      const data = await atendimentoService.getConexoes();
      setConexoes(data);
    } catch (err: any) {
      setErroMsg('Erro ao carregar conexões: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarConexoes();
    atendimentoService.getFluxos().then(setFluxos).catch(() => setFluxos([]));

    const token = localStorage.getItem('sysup_token');
    const baseUrl = env.SOCKET_URL;

    const socket = io(`${baseUrl}/atendimento`, {
      auth: { token }
    });
    socketRef.current = socket;

    socket.on('connect_error', (err) => {
      console.error('❌ ERRO NO SOCKET:', err.message);
    });

    socket.on('atendimento:conexao_status', (data: any) => {
      setConexoes(prev => prev.map(c => {
        if (c.id === data.id) {
          return {
            ...c,
            status: data.status !== undefined ? data.status : c.status,
            numero: data.numero !== undefined ? data.numero : c.numero,
            bateria: data.bateria !== undefined ? data.bateria : data.battery !== undefined ? data.battery : c.bateria
          };
        }
        return c;
      }));

      // Fecha o dialog de QR se conectou
      if (data.status === 'conectado') {
        setQrDialog(false);
        show(`✅ Conectado com sucesso!`);
      }
    });

    socket.on('atendimento:qrcode', (data: any) => {
      const { token: connToken, qrcode } = data;

      setConexoes(prev => {
        return prev.map(c => c.token === connToken ? { ...c, status: 'aguardando_qr' } : c);
      });

      if (activeQrTokenRef.current === connToken) {
        setQrCodeImg(qrcode);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const show = (msg: string, tipo: 'ok' | 'err' = 'ok') => {
    tipo === 'ok' ? setSucessoMsg(msg) : setErroMsg(msg);
    setTimeout(() => { setSucessoMsg(''); setErroMsg(''); }, 3500);
  };

  // ── CRUD ─────────────────────────────────────────────────────────────────
  const abrirNova = () => {
    setEditando(null); setForm(formVazio); setDialogTab(0); setDialogAberto(true); setMenuAnchor(null);
  };

  const abrirEditar = (c: AtendimentoConexao) => {
    setEditando(c);
    setForm({
      nome: c.nome,
      descricao: c.descricao || '',
      ativo: c.ativo,
      tipo_adapter: c.tipo_adapter,
      bot_ativo: !!c.bot_ativo,
      flow_id: c.flow_id || '',
      recuse_call: !!c.recuse_call,
      recuse_call_msg: c.recuse_call_msg || '',
      msgs_boas_vindas: !!c.msgs_boas_vindas,
      texto_boas_vindas: c.texto_boas_vindas || '',
      msgs_despedida: !!c.msgs_despedida,
      texto_despedida: c.texto_despedida || '',
      expediente_ativo: !!c.expediente_ativo,
      expediente_horarios: c.expediente_horarios || defaultBusinessHours,
      texto_fora_expediente: c.texto_fora_expediente || '',
      expediente_ignorar_saudacao: !!c.expediente_ignorar_saudacao,
      expediente_quando_enviar: c.expediente_quando_enviar === 'nova_mensagem' ? 'nova_mensagem' : 'novo_ticket'
    });
    setDialogTab(0); setDialogAberto(true); setMenuAnchor(null);
  };

  const updateExpedienteDia = (day: keyof BusinessHours, field: keyof DaySchedule, value: boolean | string) => {
    setForm((prev) => ({
      ...prev,
      expediente_horarios: {
        ...prev.expediente_horarios,
        [day]: {
          ...prev.expediente_horarios[day],
          [field]: value,
        },
      },
    }));
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setErroMsg('Nome é obrigatório.'); return; }
    try {
      if (form.bot_ativo && !form.flow_id) { setErroMsg('Selecione um flow para ativar o chatbot.'); return; }
      const payload = {
        ...form,
        flow_id: form.bot_ativo && form.flow_id ? Number(form.flow_id) : null,
      };
      if (editando) {
        await atendimentoService.updateConexao(editando.id, payload);
        show(`Conexão "${form.nome}" atualizada.`);
      } else {
        await atendimentoService.createConexao({ ...payload, tipo: 'whatsapp' });
        show(`Conexão "${form.nome}" criada.`);
      }
      setDialogAberto(false);
      carregarConexoes();
    } catch (err: any) {
      setErroMsg('Erro ao salvar: ' + err.message);
    }
  };

  const confirmarExclusao = async () => {
    if (!deleteAlvo) return;
    try {
      await atendimentoService.deleteConexao(deleteAlvo.id);
      show(`Conexão "${deleteAlvo.nome}" excluída.`);
      setDeleteDialog(false); setDeleteAlvo(null);
      carregarConexoes();
    } catch (err: any) {
      setErroMsg('Erro ao excluir: ' + err.message);
    }
  };

  // ── Ações de conexão ─────────────────────────────────────────────────────
  const abrirQR = async (c: AtendimentoConexao) => {
    setQrConexao(c);
    activeQrTokenRef.current = c.token;
    setQrDialog(true); setMenuAnchor(null); setQrCodeImg(null);
    try {
      await atendimentoService.startSession(c.id);
      setConexoes(prev => prev.map(cx => cx.id === c.id ? { ...cx, status: 'conectando' } : cx));
    } catch (err: any) {
      setErroMsg('Erro ao solicitar conexão: ' + err.message);
    }
  };

  const desconectar = async (c: AtendimentoConexao) => {
    try {
      await atendimentoService.stopSession(c.id);
      show(`Sessão parada.`);
      setMenuAnchor(null);
      carregarConexoes();
    } catch (err: any) {
      setErroMsg('Erro ao desconectar: ' + err.message);
    }
  };

  const alternarAtivo = async (c: AtendimentoConexao) => {
    const novoAtivo = !c.ativo;
    try {
      await atendimentoService.updateConexao(c.id, { ativo: novoAtivo });
      show(`Conexão "${c.nome}" ${novoAtivo ? 'ativada' : 'desativada'}.`);
      setMenuAnchor(null);
      carregarConexoes();
    } catch (err: any) {
      setErroMsg('Erro ao alterar status: ' + err.message);
    }
  };

  // ── KPIs ─────────────────────────────────────────────────────────────────
  const totalConectadas = conexoes.filter((c) => c.status === 'conectado').length;
  const totalDesconectadas = conexoes.filter((c) => c.status === 'desconectado').length;
  const totalMsgsEnviadas = conexoes.reduce((a, c) => a + c.msgs_enviadas, 0);

  // ── Render de um card de conexão ─────────────────────────────────────────
  const renderCard = (cx: AtendimentoConexao) => {
    const cfg = statusConfig[cx.status] || statusConfig['desconectado'];
    const podeConectar = cx.status === 'desconectado';
    const temBateria = typeof cx.bateria === 'number';
    const bateria = temBateria ? Math.max(0, Math.min(100, cx.bateria as number)) : 0;
    const bateriaCor = bateria < 30 ? 'error.main' : bateria < 60 ? 'warning.main' : 'success.main';

    return (
      <Grid item xs={12} sm={6} lg={4} key={cx.id}>
        <Card
          sx={{
            height: '100%', borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderTop: `3px solid ${cx.status === 'conectado' ? '#2e7d32' : cx.status === 'conectando' ? '#0277bd' : cx.status === 'aguardando_qr' ? '#e65100' : '#c62828'}`,
            opacity: cx.ativo ? 1 : 0.6,
            transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 },
          }}
        >
          <CardContent sx={{ p: 1.5, pb: 1, '&:last-child': { pb: 1 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box display="flex" alignItems="center" gap={1.2}>
                <Badge
                  overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cx.status === 'conectado' ? '#2e7d32' : cx.status === 'conectando' ? '#0277bd' : cx.status === 'aguardando_qr' ? '#e65100' : '#c62828', border: '2px solid #fff' }} />
                  }
                >
                  <Avatar sx={{ bgcolor: cfg.bgColor, width: 38, height: 38 }}>
                    <WhatsAppIcon sx={{ color: cx.status === 'conectado' ? '#2e7d32' : cx.status === 'conectando' ? '#0277bd' : '#9e9e9e', fontSize: 20 }} />
                  </Avatar>
                </Badge>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>{cx.nome}</Typography>
                  {cx.numero ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{cx.numero}</Typography>
                  ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>Sem número vinculado</Typography>
                  )}
                </Box>
              </Box>
              <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuAlvo(cx); }}>
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {cx.descricao && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1} sx={{ fontSize: '0.72rem' }}>
                {cx.descricao}
              </Typography>
            )}

            <Box display="flex" alignItems="center" gap={1} mt={1.2}>
              <Chip icon={cfg.icon as any} label={cfg.label} size="small" color={cfg.color} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
              <Chip label={cx.tipo_adapter} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              {!cx.ativo && <Chip label="Inativo" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
            </Box>

            <Divider sx={{ my: 1.2 }} />

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.62rem' }}>Enviadas</Typography>
                  <Typography variant="body2" fontWeight={700} color="primary.main">{cx.msgs_enviadas.toLocaleString('pt-BR')}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.62rem' }}>Recebidas</Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">{cx.msgs_recebidas.toLocaleString('pt-BR')}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={0.8} mt={0.5}>
                  <BatteryIcon sx={{ fontSize: 14, color: temBateria ? bateriaCor : 'text.disabled' }} />
                  <Box flex={1}>
                    <Box display="flex" justifyContent="space-between" mb={0.2}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>Bateria</Typography>
                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.62rem', color: temBateria ? bateriaCor : 'text.disabled' }}>
                        {temBateria ? `${bateria}%` : 'Sem leitura'}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={temBateria ? bateria : 0}
                      color={temBateria ? (bateria < 30 ? 'error' : bateria < 60 ? 'warning' : 'success') : 'inherit'}
                      sx={{ height: 4, borderRadius: 2, opacity: temBateria ? 1 : 0.35 }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </CardContent>

          <CardActions sx={{ px: 2, pb: 1.5, pt: 0, gap: 1 }}>
            {podeConectar ? (
              <Button fullWidth variant="contained" size="small" startIcon={<QrCodeIcon sx={{ fontSize: 16 }} />} onClick={() => abrirQR(cx)} color="primary" sx={{ fontSize: '0.78rem', textTransform: 'none', borderRadius: 2 }}>
                Conectar WhatsApp
              </Button>
            ) : cx.status === 'aguardando_qr' ? (
              <Button fullWidth variant="outlined" size="small" startIcon={<QrCodeIcon sx={{ fontSize: 16 }} />} onClick={() => abrirQR(cx)} color="warning" sx={{ fontSize: '0.78rem', textTransform: 'none', borderRadius: 2 }}>
                Ver QR Code
              </Button>
            ) : (
              <Button fullWidth variant="outlined" size="small" startIcon={<DisconnectIcon sx={{ fontSize: 16 }} />} onClick={() => desconectar(cx)} color="error" sx={{ fontSize: '0.78rem', textTransform: 'none', borderRadius: 2 }}>
                Desconectar
              </Button>
            )}
          </CardActions>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Conexões WhatsApp</Typography>
            <Typography variant="body2" color="text.secondary">Gerencie os números conectados ao sistema de atendimento</Typography>
          </Box>
        </Box>
        {!isMobile && (
          <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={abrirNova}>Nova Conexão</Button>
        )}
      </Box>

      {sucessoMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucessoMsg('')}>{sucessoMsg}</Alert>}
      {erroMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroMsg('')}>{erroMsg}</Alert>}

      {/* KPIs */}
      <Grid container spacing={1.5} mb={1.5}>
        {[
          { label: 'Total de Conexões', valor: conexoes.length, cor: 'primary.main', sub: 'cadastradas' },
          { label: 'Conectadas', valor: totalConectadas, cor: '#2e7d32', sub: 'ativas agora' },
          { label: 'Desconectadas', valor: totalDesconectadas, cor: 'error.main', sub: 'sem sinal' },
          { label: 'Msgs Enviadas', valor: totalMsgsEnviadas.toLocaleString('pt-BR'), cor: '#0277bd', sub: 'no total' },
        ].map((kpi) => (
          <Grid item xs={6} sm={3} key={kpi.label}>
            <Card>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6" fontWeight={700} color={kpi.cor}>{kpi.valor}</Typography>
                <Typography variant="caption" color="text.secondary">{kpi.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Cards de conexões */}
      {loading ? (
        <LinearProgress />
      ) : conexoes.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <WhatsAppIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>Nenhuma conexão cadastrada</Typography>
          <Typography variant="body2" color="text.disabled" mb={2}>
            Adicione um número WhatsApp para começar a receber atendimentos
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>Adicionar Conexão</Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {conexoes.map((cx) => renderCard(cx))}
        </Grid>
      )}

      {isMobile && (
        <Fab color="primary" sx={{ position: 'fixed', bottom: 86, right: 16 }} onClick={abrirNova}><AddIcon /></Fab>
      )}

      {/* ── Menu de ações ── */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}>
        {menuAlvo && (menuAlvo.status === 'desconectado' || menuAlvo.status === 'conectando') && (
          <MenuItem onClick={() => abrirQR(menuAlvo!)} sx={{ fontSize: '0.8rem' }}><QrCodeIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} /> Conectar via QR Code</MenuItem>
        )}
        {menuAlvo?.status === 'conectado' && (
          <MenuItem onClick={() => desconectar(menuAlvo!)} sx={{ fontSize: '0.8rem', color: 'error.main' }}><DisconnectIcon sx={{ fontSize: 16, mr: 1 }} /> Desconectar</MenuItem>
        )}
        {menuAlvo && (
          menuAlvo.ativo ? (
            <MenuItem onClick={() => alternarAtivo(menuAlvo!)} sx={{ fontSize: '0.8rem', color: 'warning.main' }}><BlockIcon sx={{ fontSize: 16, mr: 1 }} /> Desativar</MenuItem>
          ) : (
            <MenuItem onClick={() => alternarAtivo(menuAlvo!)} sx={{ fontSize: '0.8rem', color: 'success.main' }}><CheckCircleIcon sx={{ fontSize: 16, mr: 1 }} /> Ativar</MenuItem>
          )
        )}
        
        <MenuItem onClick={() => abrirEditar(menuAlvo!)} sx={{ fontSize: '0.8rem' }}><EditIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} /> Editar</MenuItem>
        <Divider />
        <MenuItem onClick={() => { setDeleteAlvo(menuAlvo); setDeleteDialog(true); setMenuAnchor(null); }} sx={{ fontSize: '0.8rem', color: 'error.main' }}><DeleteIcon sx={{ fontSize: 16, mr: 1 }} /> Excluir</MenuItem>
      </Menu>

      {/* Dialog criar / editar */}
      <Dialog open={dialogAberto} onClose={() => { setDialogAberto(false); setErroMsg(''); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WhatsAppIcon sx={{ color: '#25d366' }} />
            <Typography variant="h6" fontWeight={600}>{editando ? 'Editar Conexao' : 'Nova Conexao WhatsApp'}</Typography>
          </Box>
        </DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={dialogTab} onChange={(_, value) => setDialogTab(value)} variant="scrollable" scrollButtons="auto">
            <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Geral" />
            <Tab icon={<MessageIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Mensagens" />
            <Tab icon={<ScheduleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Expediente" />
          </Tabs>
        </Box>
        <DialogContent sx={{ minHeight: 450, pt: '12px !important' }}>
          {erroMsg && <Alert severity="error" onClose={() => setErroMsg('')}>{erroMsg}</Alert>}

          {dialogTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField label="Nome da conexao *" size="small" fullWidth value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} autoFocus />
              <TextField label="Descricao" size="small" fullWidth value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
              <TextField label="Engine (Adapter)" size="small" fullWidth select SelectProps={{ native: true }} value={form.tipo_adapter} onChange={(e) => setForm({ ...form, tipo_adapter: e.target.value as any })}>
                <option value="whaileys">Whaileys</option>
                <option value="baileys">Baileys</option>
                <option value="official_api">API Oficial (Cloud)</option>
              </TextField>
              <FormControlLabel control={<Switch checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} color="success" size="small" />} label={<Typography variant="body2">{form.ativo ? 'Conexao ativa' : 'Conexao inativa'}</Typography>} />
              <Divider />
              <Typography variant="subtitle2" fontWeight={700}>Chatbot Flow</Typography>
              <FormControlLabel
                control={<Switch checked={form.bot_ativo} onChange={(e) => setForm({ ...form, bot_ativo: e.target.checked, flow_id: e.target.checked ? form.flow_id : '' })} color="primary" size="small" />}
                label={<Typography variant="body2">{form.bot_ativo ? 'Flow bot ativo nesta conexao' : 'Flow bot inativo nesta conexao'}</Typography>}
              />
              <Collapse in={form.bot_ativo}>
                <TextField
                  label="Flow que sera executado"
                  size="small"
                  fullWidth
                  select
                  SelectProps={{ native: true }}
                  value={form.flow_id}
                  onChange={(e) => setForm({ ...form, flow_id: e.target.value ? Number(e.target.value) : '' })}
                  helperText={fluxos.length === 0 ? 'Nenhum flow cadastrado em Atendimento > Fluxo Bot.' : 'Este flow sera executado automaticamente nas mensagens recebidas.'}
                >
                  <option value="">Selecione um flow</option>
                  {fluxos.filter((flow) => flow.active).map((flow) => (
                    <option key={flow.id} value={flow.id}>{flow.name}</option>
                  ))}
                </TextField>
              </Collapse>
            </Box>
          )}

          {dialogTab === 1 && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Mensagem de Saudacao</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Enviada automaticamente quando um novo ticket e aberto.</Typography>
              <FormControlLabel control={<Switch checked={form.msgs_boas_vindas} onChange={(e) => setForm({ ...form, msgs_boas_vindas: e.target.checked })} color="success" size="small" />} label={<Typography variant="body2">Enviar mensagem de saudacao ao abrir ticket</Typography>} />
              <Collapse in={form.msgs_boas_vindas}>
                <TextField label="Texto da mensagem de saudacao" size="small" fullWidth multiline minRows={4} sx={{ mt: 1 }} value={form.texto_boas_vindas} onChange={(e) => setForm({ ...form, texto_boas_vindas: e.target.value })} />
                {!form.texto_boas_vindas && (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setForm({ ...form, texto_boas_vindas: defaultGreetingMessage })}
                  >
                    Usar mensagem padrao
                  </Button>
                )}
              </Collapse>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Mensagem de Despedida</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Enviada automaticamente quando o ticket e fechado.</Typography>
              <FormControlLabel control={<Switch checked={form.msgs_despedida} onChange={(e) => setForm({ ...form, msgs_despedida: e.target.checked })} color="success" size="small" />} label={<Typography variant="body2">Enviar mensagem de despedida ao fechar ticket</Typography>} />
              <Collapse in={form.msgs_despedida}>
                <TextField label="Texto da mensagem de despedida" size="small" fullWidth multiline minRows={4} sx={{ mt: 1 }} value={form.texto_despedida} onChange={(e) => setForm({ ...form, texto_despedida: e.target.value })} />
                {!form.texto_despedida && (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setForm({ ...form, texto_despedida: defaultFarewellMessage })}
                  >
                    Usar mensagem padrao
                  </Button>
                )}
              </Collapse>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Recusar Chamadas Automaticamente</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Quando habilitado, as chamadas recebidas serao recusadas automaticamente e uma mensagem sera enviada ao contato.</Typography>
              <FormControlLabel control={<Switch checked={form.recuse_call} onChange={(e) => setForm({ ...form, recuse_call: e.target.checked })} color="warning" size="small" />} label={<Typography variant="body2">Recusar chamadas automaticamente</Typography>} />
              <Collapse in={form.recuse_call}>
                <TextField label="Mensagem ao recusar chamada" size="small" fullWidth multiline minRows={4} sx={{ mt: 1 }} value={form.recuse_call_msg} onChange={(e) => setForm({ ...form, recuse_call_msg: e.target.value })} />
                {!form.recuse_call_msg && (
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setForm({ ...form, recuse_call_msg: defaultRecuseCallMessage })}
                  >
                    Usar mensagem padrao
                  </Button>
                )}
              </Collapse>
            </Box>
          )}

          {dialogTab === 2 && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Horario de Expediente</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Configure os horarios de funcionamento para cada dia da semana.</Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Prioridade de verificacao:</strong> o sistema verifica primeiro o horario configurado nesta <strong>conexao</strong>. Com o controle de expediente desativado, o atendimento funcionara 24 horas por dia, 7 dias por semana.
                </Typography>
              </Alert>

              <FormControlLabel control={<Switch checked={form.expediente_ativo} onChange={(e) => setForm({ ...form, expediente_ativo: e.target.checked })} color="primary" size="small" />} label={<Typography variant="body2">Ativar controle de expediente</Typography>} />

              <Collapse in={form.expediente_ativo}>
                <Box sx={{ mt: 2 }}>
                  {weekDays.map((day) => {
                    const daySchedule = form.expediente_horarios[day.key] || defaultBusinessHours[day.key];
                    return (
                      <Paper key={day.key} variant="outlined" sx={{ p: 2, mb: 1, bgcolor: daySchedule.enabled ? 'transparent' : 'action.disabledBackground' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={4}>
                            <FormControlLabel
                              control={<Switch checked={daySchedule.enabled} onChange={(e) => updateExpedienteDia(day.key, 'enabled', e.target.checked)} color="success" size="small" />}
                              label={<Typography variant="body2" fontWeight={500} color={daySchedule.enabled ? 'text.primary' : 'text.secondary'}>{day.label}</Typography>}
                            />
                          </Grid>
                          <Grid item xs={5} sm={3}>
                            <TextField fullWidth size="small" label="Inicio" type="time" value={daySchedule.start} onChange={(e) => updateExpedienteDia(day.key, 'start', e.target.value)} disabled={!daySchedule.enabled} InputLabelProps={{ shrink: true }} />
                          </Grid>
                          <Grid item xs={2} sm={1} sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">as</Typography>
                          </Grid>
                          <Grid item xs={5} sm={3}>
                            <TextField fullWidth size="small" label="Termino" type="time" value={daySchedule.end} onChange={(e) => updateExpedienteDia(day.key, 'end', e.target.value)} disabled={!daySchedule.enabled} InputLabelProps={{ shrink: true }} />
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Mensagem Fora do Expediente</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Mensagem enviada quando o cliente entra em contato fora do horario de atendimento.</Typography>
                    <TextField label="Texto da mensagem fora do expediente" size="small" fullWidth multiline minRows={4} value={form.texto_fora_expediente} onChange={(e) => setForm({ ...form, texto_fora_expediente: e.target.value })} />
                    {!form.texto_fora_expediente && (
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => setForm({ ...form, texto_fora_expediente: defaultOutOfHoursMessage })}
                      >
                        Usar mensagem padrao
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Comportamento Fora do Expediente</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.expediente_ignorar_saudacao}
                              onChange={(e) => setForm({ ...form, expediente_ignorar_saudacao: e.target.checked })}
                              color="primary"
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Não enviar mensagem de saudação</Typography>}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Enviar mensagem quando:"
                          value={form.expediente_quando_enviar}
                          onChange={(e) => setForm({ ...form, expediente_quando_enviar: e.target.value as 'novo_ticket' | 'nova_mensagem' })}
                        >
                          <MenuItem value="novo_ticket">Um novo ticket for criado</MenuItem>
                          <MenuItem value="nova_mensagem">Nova mensagem recebida</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDialogAberto(false); setErroMsg(''); }}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={!form.nome.trim()}>{editando ? 'Salvar' : 'Criar Conexão'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog QR Code ── */}
      <Dialog open={qrDialog} onClose={() => setQrDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <QrCodeIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>Conectar WhatsApp</Typography>
            </Box>
            <IconButton size="small" onClick={() => setQrDialog(false)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
          </Box>
          {qrConexao && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.3}>Conexão: <strong>{qrConexao.nome}</strong></Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Box sx={{ bgcolor: '#f8f9fa', borderRadius: 2, p: 1.5, mb: 2, textAlign: 'left' }}>
            <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>Como escanear:</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.72rem' }}>1. Abra o WhatsApp no seu celular<br />2. Toque em "Aparelhos conectados"<br />3. Aponte a câmera para o QR Code abaixo</Typography>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <Box sx={{ position: 'relative', width: 250, height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ddd', borderRadius: 2 }}>
              {qrCodeImg ? (
                <img src={qrCodeImg} alt="QR Code" style={{ width: '100%', height: '100%' }} />
              ) : (
                <>
                  <CircularProgress size={32} />
                  <Typography variant="caption" sx={{ mt: 2, position: 'absolute', bottom: 20 }}>Aguardando QR Code...</Typography>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Dialog confirmação de exclusão ── */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja excluir a conexão <strong>"{deleteAlvo?.nome}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setDeleteDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarExclusao}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtendimentoConexoesTab;
